import { mergeDeep } from '@sg-core/resources/assets/js/utils';
import { VideoPlayer, type VideoPlayerOptions } from '@sg-core/src/View/Components/Molecules/VideoPlayer';

export interface BackgroundVideoOptions extends VideoPlayerOptions {
  embed?: string;
  classes?: {
    controls?: string;
  };
}

export class BackgroundVideo extends VideoPlayer {
  protected controls!: HTMLElement | null;
  protected handlePlayPause!: (e: Event) => void;
  protected handleMuteToggle!: (e: Event) => void;

  constructor(el: string | Element, options: BackgroundVideoOptions = {}) {
    const embedUrl = typeof options.embed === 'string' ? options.embed.trim() : '';
    const isYoutube = embedUrl.includes('youtube.com') || embedUrl.includes('youtu.be');

    super(
      el,
      mergeDeep(
        {
          init: false,
          plyr: false,
          classes: {
            controls: '.phxsg-background-video__controls',
          },
        },
        options as Record<string, unknown>,
        isYoutube
          ? {
              autoplay: true,
              muted: true,
              plyr: true,
              plyrOptions: {
                autoplay: true,
                muted: true,
                controls: [],
                youtube: {
                  playsinline: 1,
                  rel: 0,
                  modestbranding: 1,
                  controls: 0,
                  fs: 0,
                  disablekb: 1,
                  mute: 1,
                },
              },
            }
          : {},
      ),
    );

    const controlsSelector =
      (this.options as BackgroundVideoOptions).classes?.controls || '.phxsg-background-video__controls';

    this.controls = this.element.querySelector(controlsSelector) || null;
    this.handlePlayPause = this.handlePlayPauseMethod.bind(this);
    this.handleMuteToggle = this.handleMuteToggleMethod.bind(this);

    this.init();
  }

  private handlePlayPauseMethod(e: Event) {
    const target = e.target as HTMLElement;
    const button = target.closest('[data-background-video-control="play"]');

    if (!button || !this.controls || !this.controls.contains(button)) {
      return;
    }

    this.toggle();
  }

  private handleMuteToggleMethod(e: Event) {
    const target = e.target as HTMLElement;
    const button = target.closest('[data-background-video-control="mute"]');

    if (!button || !this.controls || !this.controls.contains(button)) {
      return;
    }

    this.toggleMute();
  }

  private toggleMute() {
    const plyrInstance = this.getPlyrInstance();

    if (plyrInstance) {
      plyrInstance.muted = !plyrInstance.muted;
      this.updateMutedState(plyrInstance.muted);
    } else if (this.mediaElement instanceof HTMLVideoElement) {
      this.mediaElement.muted = !this.mediaElement.muted;
      this.updateMutedState(this.mediaElement.muted);
    }
  }

  private updateMutedState(isMuted: boolean) {
    if (isMuted) {
      this.element.classList.add('phxsg-is-muted');
    } else {
      this.element.classList.remove('phxsg-is-muted');
    }
  }

  private initMutedState() {
    const plyrInstance = this.getPlyrInstance();
    let isMuted = true;

    if (plyrInstance) {
      isMuted = plyrInstance.muted ?? true;
    } else if (this.mediaElement instanceof HTMLVideoElement) {
      isMuted = this.mediaElement.muted ?? true;
    }

    this.updateMutedState(isMuted);
  }

  init() {
    if (!this.handlePlayPause || !this.handleMuteToggle) {
      return;
    }

    this.element.addEventListener('click', this.handlePlayPause);
    this.element.addEventListener('click', this.handleMuteToggle);

    super.init();

    this.initMutedState();
  }

  destroy() {
    if (this.handlePlayPause) {
      this.element.removeEventListener('click', this.handlePlayPause);
    }

    if (this.handleMuteToggle) {
      this.element.removeEventListener('click', this.handleMuteToggle);
    }

    super.destroy();
  }

  protected onEnded(event: Event) {
    super.onEnded(event);

    const plyrInstance = this.getPlyrInstance();

    if (typeof plyrInstance?.restart === 'function') {
      plyrInstance.restart();
    }

    this.ended = false;
  }
}
